import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/schemas/auth';
import { post } from '@/lib/api-client';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { ApiError } from '@/types/api';

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setApiError('');
    try {
      await post('/auth/forgot-password', { email: data.email });
      setSubmitted(true);
    } catch (err) {
      setApiError((err as ApiError).error || 'Failed to send reset email');
    }
  };

  if (submitted) {
    return (
      <AuthLayout title="Check Your Email" subtitle="If an account exists, a reset link has been sent.">
        <p className="text-center text-sm text-text-secondary">
          Didn&apos;t receive an email? Check your spam folder or{' '}
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="text-accent-primary hover:underline"
          >
            try again
          </button>
          .
        </p>
        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-accent-primary hover:underline">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgot Password" subtitle="Enter your email to receive a reset link">
      {apiError && (
        <div role="alert" className="mb-4 rounded-md border border-accent-danger/30 bg-accent-danger/10 px-4 py-3 text-sm text-accent-danger">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full">
          Send Reset Link
        </Button>
      </form>

      <div className="mt-4 text-center">
        <Link to="/login" className="text-sm text-accent-primary hover:underline">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
